import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { urlToPattern } from "../urlToPattern";
import logo from "./logo.svg";

const legacyEnvironmentUrls: Record<string, string> = {
  "us-production": "https://rest.zuora.com",
  "us-api-sandbox": "https://rest.apisandbox.zuora.com",
  "us-central-sandbox": "https://rest.sandbox.na.zuora.com",
  "us-performance-test": "https://rest.pt1.zuora.com",
  "eu-production": "https://rest.eu.zuora.com",
  "eu-sandbox": "https://rest.sandbox.eu.zuora.com",
};

function resolveRestRootUrl(value = ""): string {
  if (value.startsWith("https://")) return value;
  return legacyEnvironmentUrls[value] ?? "https://rest.zuora.com";
}

export const zuora = defineConnector({
  identifier: "zuora",
  name: "Zuora",
  tagLine: "Subscription billing",
  logo,
  protocol: "http",
  tags: ["data"],
  defaultUrlPattern: ({ environment }) =>
    urlToPattern(
      resolveRestRootUrl(environment),
      "^https://rest\\..*\\.?zuora\\.com/.*"
    ),
  auth: oauthAuth,
  steps: [
    {
      title: "Create an OAuth client in Zuora",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Zuora uses OAuth 2.0 client credentials for API access. You'll need administrator access to create an OAuth client.

            1. Log into your Zuora tenant
            2. Navigate to **Settings** > **Administration** > **Manage Users**
            3. Click on the user you want to create API credentials for
            4. Scroll down to **OAuth Clients** and click **Create**
            5. Give your client a name (e.g., "3B Integration")
          `,
        },
      ],
    },
    {
      title: "Select your REST root URL",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Identify the Zuora tenant you are connecting in the Zuora UI, then choose the matching REST API root URL.

            [Learn more about Zuora environments](https://developer.zuora.com/docs/get-started/introduction/)
          `,
        },
        {
          kind: "select",
          label: "REST root URL",
          target: { kind: "configuration", field: "environment" },
          options: [
            { label: "US Production", value: "https://rest.zuora.com" },
            {
              label: "US Central Sandbox",
              value: "https://rest.sandbox.na.zuora.com",
            },
            { label: "US Test", value: "https://rest.test.zuora.com" },
            {
              label: "US API Sandbox",
              value: "https://rest.apisandbox.zuora.com",
            },
            {
              label: "US Performance Test",
              value: "https://rest.pt1.zuora.com",
            },
            { label: "EU Production", value: "https://rest.eu.zuora.com" },
            { label: "EU Sandbox", value: "https://rest.sandbox.eu.zuora.com" },
            { label: "EU Test", value: "https://rest.test.eu.zuora.com" },
            { label: "AP Test", value: "https://rest.test.ap.zuora.com" },
          ],
          defaultValue: "https://rest.zuora.com",
        },
      ],
    },
    {
      title: "Enter your OAuth credentials",
      items: [
        {
          kind: "markdown",
          content:
            "Copy the Client ID and Client Secret from the OAuth client you created in Zuora. The Client Secret is only shown once, so copy it immediately.",
        },
        {
          kind: "input",
          label: "Client ID",
          target: { kind: "oauthConnection", field: "clientId" },
          placeholder: "Paste your Client ID",
          required: true,
        },
        {
          kind: "input",
          label: "Client secret",
          target: { kind: "oauthConnection", field: "clientSecret" },
          placeholder: "Paste your Client secret",
          required: true,
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ environment }) =>
      `${resolveRestRootUrl(environment)}/oauth/token`,
    scopes: () => [],
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
  },
  connectionTest: {
    protocol: "http",
    request: ({ environment }) =>
      `${resolveRestRootUrl(environment)}/v1/catalog/products`,
  },
});
